import { v4 as uuid } from 'uuid';
import { AccountInterface, Call, CallData, hash, RpcProvider } from 'starknet';
import { quoteToCalls, Quote } from '@avnu/avnu-sdk';
import { DecimalFormat } from '@tempusfinance/decimal';
import { executeTransaction, getAccountAddressFromId, toHex, waitForTransactionReceipt } from '../../utils';
import { Decimal, DEFAULT_DECIMAL_PRECISION } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { Nullable } from '../../interfaces';
import { getConfigManager } from '../../config';
import { addToast } from '../toast';
import { LentDebtTokenContract } from '../../services/contracts/LentDebtTokenContract';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from './pauseBalanceRefresh';
import { repayInput } from './lendAndBorrowInputAtom';

interface RepayRequest {
  asset: Asset;
  amount: Decimal; // Amount that is passed to contract - in case user wants to repay everything, this is set to MAX decimal.
  inputAmount: Decimal; // Amount that user entered in input field
  onBehalfOf: string;
  accountId: number;
  provider: RpcProvider;
}

interface RepayStatus {
  pending: boolean;
  inputAmount: Decimal;
  success?: boolean;
  txnId?: string;
}

const lendAndBorrowRepayStatusAtom = createAsyncAtom<Nullable<RepayStatus>>(async () => null);

const lendAndBorrowRepayWithOtherAsset = async (
  inputAmount: Decimal,
  assetToRepayWith: Asset,
  walletAccount: AccountInterface,
  swapQuote: Quote,
  slippage: string,
  debtAsset: Asset,
  accountId: number,
  repayAll: boolean,
  provider: RpcProvider,
) => {
  const amountFormatted = DecimalFormat.format(inputAmount, {
    style: 'currency',
    currency: assetToRepayWith.token,
    fractionDigits: assetToRepayWith.uiTokenPrecision,
    pad: true,
    lessThanFormat: true,
  });

  const txnId = uuid();

  const swapCalldata = await quoteToCalls({
      quoteId: swapQuote.quoteId,
      takerAddress: walletAccount.address,
      slippage: Number(slippage) / 100,
      executeApprove: true,
    });

  const amountOut = new Decimal(swapQuote.buyAmount, debtAsset.tokenPrecision);

  const amountOutAdjusted = amountOut.mul(0.995);

  const approvalAmount = repayAll ? amountOutAdjusted.mul(Decimal.ONE.add(0.001)) : amountOutAdjusted;
  const approvalAmountUint256 = approvalAmount.toUint256(debtAsset.tokenPrecision);

  // If we want to repay all we don't want to reduce number of decimals to match token decimals, we want to keep amount to 18 decimals.
  // This way contract will properly recognize passed amount as Decimal.MAX_DECIMAL value.
  const amountOutAdjustedUint256 = repayAll
    ? Decimal.MAX_DECIMAL.toUint256(DEFAULT_DECIMAL_PRECISION)
    : amountOutAdjusted.toUint256(debtAsset.tokenPrecision);

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const calls = [
    // user swaps collateral asset to debt asset
    ...swapCalldata.calls,
    {
      contractAddress: debtAsset.address,
      entrypoint: 'approve',
      calldata: [debtAsset.debtTokenAddress, approvalAmountUint256.low, approvalAmountUint256.high],
    },
    {
      contractAddress: debtAsset.debtTokenAddress,
      entrypoint: 'burn',
      calldata: [accountAddress, amountOutAdjustedUint256.low, amountOutAdjustedUint256.high],
    },
  ] as Call[];

  pauseBalanceRefresh();
  try {
    lendAndBorrowRepayStatusAtom.setValue({
      pending: true,
      inputAmount,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await executeTransaction(walletAccount, calls);
    } catch (error) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Repay with ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    lendAndBorrowRepayStatusAtom.setValue({
      pending: false,
      success: true,
      inputAmount,
      txnId,
    });

    emitAtomEvent('lend-and-borrow-repay');

    repayInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Repaid **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

const lendAndBorrowRepay = async (walletAccount: AccountInterface, request: RepayRequest) => {
  const { asset, amount, inputAmount, accountId, provider } = request;
  const txnId = uuid();

  const debtTokenContract = new LentDebtTokenContract(asset.tokenPrecision, asset.debtTokenAddress, walletAccount);

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const amountFormatted = DecimalFormat.format(inputAmount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
    lessThanFormat: true,
  });

  pauseBalanceRefresh();
  try {
    lendAndBorrowRepayStatusAtom.setValue({
      pending: true,
      inputAmount,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await debtTokenContract.burn(asset, amount, inputAmount, accountAddress, walletAccount);
    } catch (error) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Repay ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    lendAndBorrowRepayStatusAtom.setValue({
      pending: false,
      success: true,
      inputAmount,
      txnId,
    });

    emitAtomEvent('lend-and-borrow-repay');

    repayInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Repaid **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

const lendAndBorrowRepayWithCollateral = async (
  collateralAmount: Decimal,
  account: AccountInterface,
  accountId: number,
  collateralAsset: Asset,
  debtAsset: Asset,
  collateralToken: string,
  swapQuote: Quote | null,
  slippage: string,
  repayAll: boolean,
  useAllCollateral: boolean,
  provider: RpcProvider,
) => {
  const config = getConfigManager().getConfig();

  const adapterAddress = config.deferredBatchCallAdapter;
  const accountAddress = getAccountAddressFromId(account.address, accountId);

  const collateralAmountToApprove = useAllCollateral ? collateralAmount.mul(Decimal.ONE.add(0.01)) : collateralAmount;
  const collateralAmountToApproveUint256 = collateralAmountToApprove.toUint256(collateralAsset.tokenPrecision);

  const collateralAmountToWithdrawUint256 = useAllCollateral
    ? Decimal.MAX_DECIMAL.toUint256(DEFAULT_DECIMAL_PRECISION)
    : collateralAmount.toUint256(collateralAsset.tokenPrecision);

  // if same asset, no need to swap
  const swapCalldata =
    collateralAsset.address !== debtAsset.address && swapQuote
      ? await quoteToCalls({
          quoteId: swapQuote.quoteId,
          takerAddress: adapterAddress,
          slippage: Number(slippage) / 100,
          executeApprove: true,
        })
      : null;

  const amountOut = swapQuote ? new Decimal(swapQuote.buyAmount, debtAsset.tokenPrecision) : collateralAmount;

  const amountOutAdjusted = amountOut.mul(0.995);

  const approvalAmount = repayAll ? amountOutAdjusted.mul(Decimal.ONE.add(0.001)) : amountOutAdjusted;
  const approvalAmountUint256 = approvalAmount.toUint256(debtAsset.tokenPrecision);

  // If we want to repay all we don't want to reduce number of decimals to match token decimals, we want to keep amount to 18 decimals.
  // This way contract will properly recognize passed amount as Decimal.MAX_DECIMAL value.
  const amountOutAdjustedUint256 = repayAll
    ? Decimal.MAX_DECIMAL.toUint256(DEFAULT_DECIMAL_PRECISION)
    : amountOutAdjusted.toUint256(debtAsset.tokenPrecision);

  const burnAndMintAccountsToDefer = [account.address, adapterAddress];

  const burnAndMintCalls = [
    // transfer nostra tokens to adapter so he can withdraw
    {
      to: collateralToken,
      selector: hash.getSelectorFromName('transferFrom'),
      calldata: [
        accountAddress,
        adapterAddress,
        collateralAmountToWithdrawUint256.low,
        collateralAmountToWithdrawUint256.high,
      ],
    },
    // withdraw asset to adapter
    {
      to: collateralToken,
      selector: hash.getSelectorFromName('burn'),
      calldata: [
        adapterAddress,
        adapterAddress,
        collateralAmountToWithdrawUint256.low,
        collateralAmountToWithdrawUint256.high,
      ],
    },
    // adapter swaps collateral asset to debt asset
    ...(swapCalldata
      ? swapCalldata.calls.map(swapCall => {
          return {
            to: swapCall.contractAddress,
            selector: hash.getSelectorFromName(swapCall.entrypoint),
            calldata: swapCall.calldata,
          };
        })
      : []),
    // adapter repays user debt
    {
      to: debtAsset.address,
      selector: hash.getSelectorFromName('approve'),
      calldata: [debtAsset.debtTokenAddress, approvalAmountUint256.low, approvalAmountUint256.high],
    },
    {
      to: debtAsset.debtTokenAddress,
      selector: hash.getSelectorFromName('burn'),
      calldata: [accountAddress, amountOutAdjustedUint256.low, amountOutAdjustedUint256.high],
    },
  ];

  const calls = [
    {
      contractAddress: collateralToken,
      entrypoint: 'approveFromSubAccount',
      calldata: CallData.compile([
        accountAddress,
        adapterAddress,
        collateralAmountToApproveUint256.low,
        collateralAmountToApproveUint256.high,
      ]).map(data => toHex(data)),
    },
    {
      contractAddress: adapterAddress,
      entrypoint: 'batch',
      calldata: CallData.compile([burnAndMintAccountsToDefer, burnAndMintCalls]).map(data => toHex(data)),
    },
  ] as Call[];

  const txnId = uuid();

  const collateralAmountFormatted = DecimalFormat.format(collateralAmount, {
    style: 'currency',
    currency: collateralAsset.token,
    fractionDigits: collateralAsset.uiTokenPrecision,
    pad: true,
    lessThanFormat: true,
  });

  pauseBalanceRefresh();
  try {
    lendAndBorrowRepayStatusAtom.setValue({
      pending: true,
      inputAmount: collateralAmount,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await executeTransaction(account, calls);
    } catch (error) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount: collateralAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${collateralAmountFormatted} collateral`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Repay with ${collateralAmountFormatted} collateral`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount: collateralAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${collateralAmountFormatted} collateral`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(transactionResponse.transactionHash, account, provider);

    if (transactionReceipt.isReverted()) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount: collateralAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${collateralAmountFormatted} collateral`,
        txId: transactionResponse.transactionHash,
      });
    }

    lendAndBorrowRepayStatusAtom.setValue({
      pending: false,
      success: true,
      inputAmount: collateralAmount,
      txnId,
    });

    emitAtomEvent('lend-and-borrow-repay');

    repayInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Repaid with **${collateralAmountFormatted}** collateral`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

const lendAndBorrowRepayWithSameCollateralAsset = async (
  inputAmount: Decimal,
  walletAccount: AccountInterface,
  accountId: number,
  asset: Asset,
  collateralToken: string,
  repayAll: boolean,
  useAllCollateral: boolean,
  provider: RpcProvider,
) => {
  const config = getConfigManager().getConfig();

  const adapterAddress = config.deferredBatchCallAdapter;

  const amountFormatted = DecimalFormat.format(inputAmount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
    lessThanFormat: true,
  });

  const repayAllBufferMultiplier = Decimal.ONE.add(0.0001);

  // In case of partial repay, we can withdraw exact amount of collateral.
  // In case of full repay - we need to withdraw with a small buffer because debt is constantly increasing.
  const withdrawAmountUint256 = useAllCollateral
    ? Decimal.MAX_DECIMAL.toUint256(DEFAULT_DECIMAL_PRECISION)
    : repayAll
    ? inputAmount.mul(repayAllBufferMultiplier).toUint256(asset.tokenPrecision)
    : inputAmount.toUint256(asset.tokenPrecision);

  const approvalAmount = repayAll ? inputAmount.mul(repayAllBufferMultiplier) : inputAmount;
  const approvalAmountUint256 = approvalAmount.toUint256(asset.tokenPrecision);

  // If we want to repay all we don't want to reduce number of decimals to match token decimals, we want to keep amount to 18 decimals.
  // This way contract will properly recognize passed amount as Decimal.MAX_DECIMAL value.
  const repayAmountUint256 = repayAll
    ? Decimal.MAX_DECIMAL.toUint256(DEFAULT_DECIMAL_PRECISION)
    : inputAmount.toUint256(asset.tokenPrecision);

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const burnAndMintAccountsToDefer = [accountAddress, adapterAddress];

  const burnAndMintCalls = [
    // transfer nostra tokens to adapter so he can withdraw
    {
      to: collateralToken,
      selector: hash.getSelectorFromName('transferFrom'),
      calldata: [accountAddress, adapterAddress, withdrawAmountUint256.low, withdrawAmountUint256.high],
    },
    // withdraw asset to adapter
    {
      to: collateralToken,
      selector: hash.getSelectorFromName('burn'),
      calldata: [adapterAddress, adapterAddress, withdrawAmountUint256.low, withdrawAmountUint256.high],
    },
    // adapter repays user debt
    {
      to: asset.address,
      selector: hash.getSelectorFromName('approve'),
      calldata: [asset.debtTokenAddress, approvalAmountUint256.low, approvalAmountUint256.high],
    },
    {
      to: asset.debtTokenAddress,
      selector: hash.getSelectorFromName('burn'),
      calldata: [accountAddress, repayAmountUint256.low, repayAmountUint256.high],
    },
  ];

  const calls = [
    {
      contractAddress: collateralToken,
      entrypoint: 'approveFromSubAccount',
      calldata: CallData.compile([
        accountAddress,
        adapterAddress,
        withdrawAmountUint256.low,
        withdrawAmountUint256.high,
      ]).map(data => toHex(data)),
    },
    {
      contractAddress: adapterAddress,
      entrypoint: 'batch',
      calldata: CallData.compile([burnAndMintAccountsToDefer, burnAndMintCalls]).map(data => toHex(data)),
    },
  ] as Call[];

  const txnId = uuid();

  pauseBalanceRefresh();
  try {
    lendAndBorrowRepayStatusAtom.setValue({
      pending: true,
      inputAmount,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await executeTransaction(walletAccount, calls);
    } catch (error) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Repay with ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      lendAndBorrowRepayStatusAtom.setValue({
        pending: false,
        success: false,
        inputAmount,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Repay with ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    lendAndBorrowRepayStatusAtom.setValue({
      pending: false,
      success: true,
      inputAmount,
      txnId,
    });

    emitAtomEvent('lend-and-borrow-repay');

    repayInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Repaid **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

export {
  lendAndBorrowRepayStatusAtom,
  lendAndBorrowRepay,
  lendAndBorrowRepayWithCollateral,
  lendAndBorrowRepayWithOtherAsset,
  lendAndBorrowRepayWithSameCollateralAsset,
};
